import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppSelector } from '@/redux/Store';

/**
 * Gate for interaction handlers on ADR-007 public pages.
 *
 * Signed-in users pass through untouched. Signed-out users see a toast
 * inviting them to sign up and get sent to /signup with a `from`
 * state so the signup flow can bounce them back after account creation.
 *
 * Usage:
 *
 *   const { requireAuth } = useSignupGuard();
 *
 *   const handleLike = async () => {
 *     if (!requireAuth('Sign up to like posts')) return;
 *     // ... existing like logic
 *   };
 *
 * Returns `true` when the caller should proceed; `false` when the guard
 * fired and the caller should abort. Keeps the handler shape flat —
 * no nested callbacks — so existing optimistic-update logic reads
 * the same as it did before.
 */
export function useSignupGuard() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthed = useAppSelector((s) => !!s.auth?.user?.id);

  const requireAuth = useCallback(
    (message: string = 'Sign up to interact') => {
      if (isAuthed) return true;
      toast.info(message, { autoClose: 5000 });
      navigate('/signup', { state: { from: location.pathname + location.search } });
      return false;
    },
    [isAuthed, navigate, location.pathname, location.search],
  );

  return { isAuthed, requireAuth };
}
